/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops ダッシュボード「内訳」タブのメンバー別テーブル。期間・サービス絞り込み済みの依頼を担当者別に、対応件数・工数 pt・期限超過率・平均処理日数（完了済みの起票→完了の営業日）で並べる。担当は現在の担当者で集計し、未割当は末尾にまとめる。SLO 達成率が目標ラインを割り込む担当者は期限超過率を赤で示す（判定は SLO タブの色分けと同じ ops-core の isOpsSloRateOnTarget）
 * @graph-connects aggregateOpsByMember [calls] 担当者別の集計
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { aggregateOpsByMember } from '@cortex/ops-core';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { formatOpsMetric } from '../../../lib/ops-metric-format.js';
import type { OpsRequest } from '../../../types/ops.js';

/** OpsMemberBreakdownCard の props。 */
export interface OpsMemberBreakdownCardProps {
  /** 期間・サービス絞り込み済みの依頼（SLO タブと同じ集合）。 */
  rows: OpsRequest[];
  /** 見出しに出す期間ラベル。 */
  periodLabel: string;
  /** 未解決依頼の経過を測る基準時刻（SLO タブと同じ描画内の時刻を渡し、タブ間で判定をそろえる）。 */
  now: Date;
}

/**
 * メンバー別テーブル。依頼が無ければ空状態を出す。
 * @graph-connects aggregateOpsByMember [calls] 担当者別の集計
 */
export function OpsMemberBreakdownCard({
  rows,
  periodLabel,
  now,
}: OpsMemberBreakdownCardProps): ReactNode {
  const { t } = useTranslation();
  const members = aggregateOpsByMember(rows, now);
  return (
    <Card padding="none">
      <div className="border-b border-[var(--color-border)] px-4 py-3">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('ops.dashboard.byMember.title', { period: periodLabel })}
        </h2>
        <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
          {t('ops.dashboard.byMember.note')}
        </p>
      </div>
      {members.length === 0 ? (
        <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">
          {t('ops.dashboard.byMember.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="text-left text-xs text-[var(--color-text-muted)]">
                <th className="px-4 py-2 font-medium">
                  {t('ops.dashboard.byMember.columns.member')}
                </th>
                <th className="px-2 py-2 font-medium">
                  {t('ops.dashboard.byMember.columns.count')}
                </th>
                <th className="px-2 py-2 font-medium">
                  {t('ops.dashboard.byMember.columns.points')}
                </th>
                <th className="px-2 py-2 font-medium">
                  {t('ops.dashboard.byMember.columns.overdueRate')}
                </th>
                <th className="px-2 py-2 font-medium">
                  {t('ops.dashboard.byMember.columns.avgDays')}
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr
                  key={m.member ?? '__unassigned'}
                  className="border-t border-[var(--color-border)]"
                >
                  <td className="px-4 py-2.5 font-medium text-[var(--color-text-primary)]">
                    {m.member ?? t('ops.dashboard.byMember.unassigned')}
                  </td>
                  <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">{m.count}</td>
                  <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">{m.points}</td>
                  <td
                    className={`px-2 py-2.5 ${
                      m.meetsSloTarget === false
                        ? 'font-semibold text-[var(--color-error)]'
                        : 'text-[var(--color-text-secondary)]'
                    }`}
                  >
                    {formatOpsMetric(m.overdueRate, true)}
                  </td>
                  <td className="px-2 py-2.5 text-[var(--color-text-secondary)]">
                    {m.avgProcessingDays === null
                      ? formatOpsMetric(null)
                      : t('ops.dashboard.byMember.days', {
                          count: m.avgProcessingDays,
                          days: m.avgProcessingDays,
                        })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
